import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useIntl } from 'react-intl';
import { Form, Spin } from 'antd';
import dayjs from 'dayjs';
import { Button } from '../../Button';
import { Input } from '../../Input';
import { Label } from '../../Label';
import { Tag } from '../../Tag';
import { StyledChatDrawer } from './style';
import { ChatMessage, ChatMessageData, ChatDayDivider } from '../../Chat';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import { useAuth } from 'hooks/useAuth';
import {
  useRealtimeMessages,
  MessagePayload,
} from 'realtime/useRealtimeMessages';

interface DrawerChatProps {
  open: boolean;
  conversationId?: string | null;
  recipientFarmId: number | null;
  onClose: () => void;
}

interface RecipientFarm {
  id: number;
  name: string;
  brand?: string;
}

interface ConversationResponse {
  data: ChatMessageData[];
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
  recipient_farm: RecipientFarm | null;
}

export const DrawerChat: React.FC<DrawerChatProps> = ({
  open,
  conversationId = null,
  recipientFarmId,
  onClose,
}) => {
  const intl = useIntl();
  const { user } = useAuth();
  const farmId = user?.farmId ?? user?.farm?.id;

  const [messages, setMessages] = useState<ChatMessageData[]>([]);
  const [recipientFarm, setRecipientFarm] = useState<RecipientFarm | null>(
    null
  );
  const [inputValue, setInputValue] = useState('');

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const bulkReadRef = useRef<
    (
      data: { ids: string[] },
      urlParams?: Record<string, string>,
      passOnSuccess?: string[]
    ) => void
  >(() => {});

  const fetchUrl = conversationId
    ? `/api/v2/messages/conversations/${conversationId}`
    : recipientFarmId
      ? `/api/v2/messages/conversation/${recipientFarmId}`
      : null;

  const {
    isLoading,
  } = useQueryApiClient({
    request: {
      url: fetchUrl ?? '',
      data: { per_page: 100 },
    },
    enabled: open && !!fetchUrl,
    onSuccess: (response: ConversationResponse) => {
      if (response?.data) {
        setMessages(response.data);
      }
      if (response?.recipient_farm) {
        setRecipientFarm(response.recipient_farm);
      }
    },
  });

  const { appendData: bulkRead } = useQueryApiClient({
    request: {
      url: '/api/v2/messages/bulk-read',
      method: 'POST',
    },
    onSuccess: (_response: any, ids: string[]) => {
      if (ids?.length) {
        const now = new Date().toISOString();
        setMessages((prev) =>
          prev.map((m) =>
            ids.includes(m.id) ? { ...m, is_read: true, read_at: now } : m
          )
        );
      }
    },
  });
  bulkReadRef.current = bulkRead;

  const sendUrl = conversationId
    ? `/api/v2/messages/conversations/${conversationId}`
    : recipientFarmId
      ? `/api/v2/messages/conversation/${recipientFarmId}`
      : null;

  const { appendData: sendMessage, isLoading: isSending } = useQueryApiClient({
    request: {
      url: sendUrl ?? '',
      method: 'POST',
    },
    onSuccess: (response) => {
      if (response?.message) {
        setMessages((prev) => [...prev, response.message]);
        setInputValue('');
      }
    },
  });

  const handleMessageCreated = useCallback(
    (payload: MessagePayload) => {
      if (!farmId) return;

      const msg = payload.message;

      if (Number(msg.sender_farm_id) === Number(farmId)) {
        return;
      }

      const isForCurrentChat =
        conversationId && msg.conversation_id
          ? msg.conversation_id === conversationId &&
            Number(msg.recipient_farm_id) === Number(farmId)
          : recipientFarmId &&
            Number(msg.sender_farm_id) === Number(recipientFarmId) &&
            Number(msg.recipient_farm_id) === Number(farmId);

      if (isForCurrentChat) {
        setMessages((prev) => {
          const exists = prev.some((m) => m.id === msg.id);
          if (exists) return prev;

          const newMessage: ChatMessageData = {
            id: msg.id,
            body: msg.body,
            subject: undefined,
            created_at: msg.created_at,
            is_read: false,
            read_at: null,
            is_sent: false,
            sender_farm_id: msg.sender_farm_id,
            recipient_farm_id: msg.recipient_farm_id,
            sender_user_name: msg.sender_user_name,
            sender_farm: null,
            recipient_farm: null,
          };

          return [...prev, newMessage];
        });
      }
    },
    [conversationId, recipientFarmId, farmId]
  );

  const handleMessagesRead = useCallback((messageIds: string[]) => {
    if (!messageIds?.length) return;
    const now = new Date().toISOString();
    setMessages((prev) =>
      prev.map((m) =>
        messageIds.includes(m.id) ? { ...m, is_read: true, read_at: now } : m
      )
    );
  }, []);

  useRealtimeMessages({
    farmId,
    onMessageCreated: handleMessageCreated,
    onMessagesRead: handleMessagesRead,
  });

  useEffect(() => {
    if (open) {
      setMessages([]);
      setRecipientFarm(null);
      setInputValue('');
    }
  }, [open, conversationId, recipientFarmId]);

  useEffect(() => {
    if (open && !!farmId && messages.length > 0) {
      const unreadIds = messages
        .filter(
          (m) => Number(m.recipient_farm_id) === Number(farmId) && !m.is_read
        )
        .map((m) => m.id);
      if (unreadIds.length > 0) {
        bulkReadRef.current({ ids: unreadIds }, undefined, unreadIds);
      }
    }
  }, [open, farmId, messages]);

  const handleSend = useCallback(() => {
    if (!inputValue.trim() || isSending || !(conversationId || recipientFarmId))
      return;

    sendMessage({ body: inputValue.trim() });
  }, [inputValue, isSending, conversationId, recipientFarmId, sendMessage]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const chatFooter = (
    <Form>
      <div className="chat-input-area">
        <div className="chat-input-wrapper">
          <Input
            noStyle
            placeholder={intl.formatMessage({
              id: 'chat.message_placeholder',
            })}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isSending}
            className="chat-input"
          />
        </div>
        <Button
          type="primary"
          htmlType="button"
          label={intl.formatMessage({ id: 'chat.send' })}
          onClick={handleSend}
          disabled={!inputValue.trim() || isSending}
          loading={isSending}
          className="chat-send-btn"
        />
      </div>
    </Form>
  );

  const messagesWithDividers = useMemo(() => {
    const result: Array<{
      type: 'divider' | 'message';
      date?: string;
      message?: ChatMessageData;
    }> = [];
    let lastDate = '';

    messages.forEach((message) => {
      const messageDate = dayjs(message.created_at).format('YYYY-MM-DD');

      if (messageDate !== lastDate) {
        result.push({ type: 'divider', date: message.created_at });
        lastDate = messageDate;
      }

      result.push({ type: 'message', message });
    });

    return result;
  }, [messages]);

  const chatSubject =
    messages.length > 0 && messages[0].subject
      ? messages[0].subject
      : intl.formatMessage({ id: 'chat.correspondence' });

  const chatTitle = (
    <div className="chat-header">
      <Label label={chatSubject} title className="chat-header-title" />
      <Label
        label={recipientFarm?.brand || recipientFarm?.name}
        className="chat-header-brand"
      />
      <Tag className="chat-header-tag">
        {intl.formatMessage({ id: 'chat.correspondence' })}
      </Tag>
    </div>
  );

  return (
    <StyledChatDrawer
      open={open}
      onClose={onClose}
      title={chatTitle}
      width={1000}
      destroyOnClose
      footer={chatFooter}
      zIndex={1100}
    >
      <div className="chat-messages-container" ref={messagesContainerRef}>
        <div className="chat-messages-inner">
          {isLoading ? (
            <div className="chat-messages-loading">
              <Spin size="large" />
            </div>
          ) : messages.length === 0 ? (
            <div className="chat-messages-empty">
              {intl.formatMessage({ id: 'messages.no_messages' })}
            </div>
          ) : (
            <>
              {messagesWithDividers.map((item, index) => {
                if (item.type === 'divider' && item.date) {
                  return (
                    <ChatDayDivider
                      className="chat-day-divider"
                      key={`divider-${index}`}
                      date={item.date}
                    />
                  );
                }
                if (item.type === 'message' && item.message) {
                  return (
                    <ChatMessage
                      key={item.message.id}
                      message={item.message}
                      isOwnMessage={
                        item.message.is_sent ||
                        item.message.sender_farm_id === farmId
                      }
                    />
                  );
                }
                return null;
              })}
            </>
          )}
        </div>
      </div>
    </StyledChatDrawer>
  );
};

export default DrawerChat;
